import { Stack, useDisclosure } from '@chakra-ui/react'
import SubmitDialog from 'BrokerTsLib/src/components/SubmitDialog'
import type { InsuranceParamsDto } from 'BrokerTsLib/src/dtos'
import { InitialGeneralParamsValues, InitialPoleasVehicleValues, InitialPolicyHolderValues, InitialVehicleValues } from 'BrokerTsLib/src/dtos'
import { getInsuranceParamsTypeName, InsuranceParamsType, Product } from 'BrokerTsLib/src/enums'
import * as global from 'brokertslib/src/helpers/global'
import { useInsuranceParamsFormPersistance } from 'BrokerTsLib/src/hooks'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import { formatDate } from 'BrokerTsLib/src/Utils/formatPrice'
import { GeneralParamsValidationSchema, PolicyHolderValidationSchema, VehicleValidationSchema } from 'BrokerTsLib/src/validation'
import cloneDeep from 'lodash/cloneDeep'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import * as yup from 'yup'

import { ContractReplacementModal } from '@/components/ContractReplacement'
import { CustomWaypoint } from '@/components/CustomWaypoint'
import ErrorSummary from '@/components/ErrorSummary'
import { PageSection } from '@/components/PageSection'
import { UnsavedFormModal } from '@/components/UnsavedFormModal'
import { FormSection } from '@/containers/forms/FormSection'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { usePartner } from '@/context/PartnerContext'
import { useSidebar } from '@/context/SidebarContext'
import { useQueryString } from '@/hooks'
import { useSaveStep1, useUpdateLastWizardStep } from '@/hooks/useWizard'
import type { PageSection as Section } from '@/layouts/components/Sidebar'

import { Information } from './containers/Information'
import { Insuree } from './containers/Insuree'
import { Vehicle } from './containers/Vehicle'

export const schema = yup.object().shape(
  {
    generalParams: GeneralParamsValidationSchema,
    vehicle: VehicleValidationSchema,
    policyHolder: PolicyHolderValidationSchema,
  }
) as any

export type Step1FormValues = Omit<InsuranceParamsDto, 'baseParams'> // base params je az na K2

export type Step1FormProps = {
  data?: Step1FormValues
  type?: InsuranceParamsType | undefined
}

const defaultValues: Step1FormValues = {
  nextPage: 'next',
  // baseParams: InitialBaseParamsValues,
  generalParams: InitialGeneralParamsValues,
  policyHolder: InitialPolicyHolderValues,
  vehicle: InitialVehicleValues
}

export const Step1 = ({ data = defaultValues, type }: Step1FormProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const { triggerNewInsurance, clearTriggerNewInsurance, refetchInsuranceParams, insuranceParams, insuranceParamsId: paramsId, contractReplacement, clearContractReplacement, isDisabled } = useInsuranceParams()
  const navigate = useNavigate()

  const queryString = useQueryString()
  const validationSummaryRef = useRef<HTMLDivElement>(null)

  let defaultFormData = { ...data, type: type ?? InsuranceParamsType.Basic }

  const { isOpen: isOpenContractReplacementModal, onClose: onCloseContractReplacementModal, onOpen: onOpenContractReplacementModal } = useDisclosure()
  useEffect(() => {
    if (contractReplacement) {
      onOpenContractReplacementModal()
      clearContractReplacement()
    }
  }, [contractReplacement])

  const newPoleas = queryString.get('newPoleas')
  if (newPoleas == '1') {
    defaultFormData = {
      ...data,
      type: type ?? InsuranceParamsType.Basic,
      importedData: {},
      vehicle: InitialPoleasVehicleValues
    }
  }

  const methods = useForm<Step1FormValues>({
    values: defaultFormData,
    resolver: yupResolver(schema),
    mode: 'onBlur'
  })

  const {
    formState: { errors },
    handleSubmit,
    setValue,
    reset,
    trigger,
    setError
  } = methods

  const { mutateAsync: updateStep } = useUpdateLastWizardStep()
  const { mutateAsync: saveStep1 } = useSaveStep1()

  // v nevalidniho formulare se nascroluje na validationSummary
  const scrollToValidationSummary = () => {
    setTimeout(() => {
      validationSummaryRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 0)
  }

  const insuranceParamsFormPersistance = useInsuranceParamsFormPersistance(Product.VehicleInsurance, 1, paramsId, methods)

  const submitForm = useCallback(handleSubmit((data) => {
    setIsLoading(true)
    const values = cloneDeep(data)

    // Datum poslu jen jako date bez casu. To by chtelo vyresit asi nejak obecne. Kdyz se to posle i s casem, tak dochazi k casovemu posunu, coz znamena na serveru predchozi den
    values.generalParams.insuranceStartDate = formatDate(values.generalParams.insuranceStartDate)
    values.vehicle.firstRegistrationDate = formatDate(values.vehicle.firstRegistrationDate)
    values.vehicle.firstRegistrationDateCz = formatDate(values.vehicle.firstRegistrationDateCz)
    values.vehicle.purchaseDate = formatDate(values.vehicle.purchaseDate)
    values.policyHolder.person.birthday = formatDate(values.policyHolder.person.birthday)
    if (values.policyHolder.owner) values.policyHolder.owner.birthday = formatDate(values.policyHolder.owner.birthday)
    if (values.policyHolder.holder) values.policyHolder.holder.birthday = formatDate(values.policyHolder.holder.birthday)

    // ten jejich RadioIcon a select neumi s intem
    values.vehicle.druh = values.vehicle.druh ? parseInt(values.vehicle.druh.toString()) as any : undefined
    values.vehicle.palivo = values.vehicle.palivo ? parseInt(values.vehicle.palivo.toString()) as any : undefined
    values.vehicle.usage = values.vehicle.usage ? parseInt(values.vehicle.usage.toString()) as any : undefined

    return saveStep1(values as InsuranceParamsDto)
      .then((response) => {
        reset(data)
        insuranceParamsFormPersistance.clear()

        return (async () => {
          if (values.nextPage == 'next') {
            if ((insuranceParams?.generalParams.lastWizardStep ?? 1) <= 1)
              await updateStep({ paramsId: Number(response?.data), step: 2 })

            if (paramsId)
              await refetchInsuranceParams()

            // aby se stihl resetovat formular a smazal local storage pred navigaci (jinak se zobrazuje okno s neulozenymi daty)
            setTimeout(() => {
              navigate(`/steps/2/${response?.data}`)
            }, 0)
          }
          else {
            if (paramsId)
              await refetchInsuranceParams()

            savedFormAlertOnOpen()
          }

          setIsLoading(false)
        })()
      })
      .catch((err) => {
        setIsLoading(false)

        if (!(err.response && err.response.status === 400 && err.response.data))
          return

        const data = err.response.data

        // zobrazeni serverove validace
        Object.keys(data).forEach((x: any) => {
          setError(global.toCamelCase(x), { message: data[x].join(' ') })
        })

        scrollToValidationSummary()
      })
  }, (err) => {
    scrollToValidationSummary()
  }), [handleSubmit])

  // pro pripad kdyz se z K1 klidne na 'Nové pojištění', tak se musi formular resetnout. Jinak nedochazelo k rerenderu
  useEffect(() => {
    if (triggerNewInsurance)
      reset(defaultFormData)

    clearTriggerNewInsurance()
  }, [triggerNewInsurance])

  const { pageSections, setPageSections } = useSidebar()
  useEffect(() => {
    let i = 1
    const array: Section[] = [
      {
        id: 'vozidlo',
        sectionTitle: 'Vozidlo',
        index: i++,
        children: <Vehicle />
      },
      {
        id: 'pojistnik',
        sectionTitle: 'Pojistník',
        index: i++,
        children: <Insuree />
      },
      {
        id: 'informace',
        sectionTitle: 'Obecné informace',
        index: i++,
        children: (
          <Information
            onContinue={() => {
              setValue('nextPage', 'next')
              return submitForm()
            }}
            onSave={() => {
              setValue('nextPage', 'current')
              return submitForm()
            }}
            isLoading={isLoading}
          />
        )
      },
    ]

    setPageSections(array)
  }, [isLoading, submitForm])

  const {
    isOpen: isSavedFormAlertOpen,
    onOpen: savedFormAlertOnOpen,
    onClose: savedFormAlertOnClose,
  } = useDisclosure()

  const { currentPartner } = usePartner()
  const isEssox = ['Essox', 'AutoEsa', 'DirectAuto'].includes(currentPartner?.partner ?? '')

  return (
    <FormProvider {...methods}>
      <form onSubmit={submitForm}>
        <Stack gap={8}>

          {isEssox && (type ?? 0) > 1 && (
            <FormSection sectionTitle={getInsuranceParamsTypeName(type)} />
          )}

          {pageSections.map(x => (
            <CustomWaypoint key={x.id} sectionId={x.id}>
              <PageSection id={x.id}>
                {x.children}
              </PageSection>
            </CustomWaypoint>
          ))}

          <ErrorSummary errors={errors} ref={validationSummaryRef} />

          {/* nechci zobrazovat dva modaly pres sebe */}
          {!isOpenContractReplacementModal && <UnsavedFormModal {...insuranceParamsFormPersistance} />}

          <SubmitDialog
            isOpen={isSavedFormAlertOpen}
            onClose={savedFormAlertOnClose}
            header='Uloženo'
            body={!paramsId
              ? 'Nabídka byla uložena do rozpracovaných kalkulací'
              : 'Nabídka byla úspěšně uložena'
            }
          />

          <ContractReplacementModal
            isOpen={isOpenContractReplacementModal}
            onClose={onCloseContractReplacementModal}
            modalContentProps={{ sx: { w: '100%', maxW: '800px' } }}
            title='Náhrada smlouvy'
          />
        </Stack>
      </form>
    </FormProvider >
  )
}
